import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  NUDGE_REASON_LABELS,
  type NudgeReason,
  type TastingDashboard as Dashboard,
  type TastingDashboardRow,
} from '@gwc/shared';
import { dashboardApi } from '../api/dashboard';
import { CountTile } from '../components/dashboard/CountTile';
import { DashboardRow } from '../components/dashboard/DashboardRow';
import { DASHBOARD_GRID, errorMessage } from '../components/dashboard/layout';
import { NudgeDialog } from '../components/dashboard/NudgeDialog';
import { RegisterSomeoneButton } from '../components/dashboard/RegistrationActions';
import { TestEventBadge } from '../components/TestEventBadge';
import { Button } from '../components/ui';
import { ApiError } from '../lib/api';
import { PHASE_LABEL } from '../lib/format';
import { toast } from '../lib/toast';

type Filter = 'all' | 'unpaid' | 'no_wine' | 'no_label' | 'unreviewed' | 'no_letter' | 'waitlist';

const FILTER_TEST: Record<Filter, (r: TastingDashboardRow) => boolean> = {
  all: () => true,
  unpaid: (r) => r.gaps.includes('unpaid'),
  no_wine: (r) => r.gaps.includes('no_wine'),
  no_label: (r) => r.gaps.includes('no_label'),
  unreviewed: (r) => r.hasWine && !r.wineReviewed,
  no_letter: (r) => r.status === 'registered' && !r.slot,
  waitlist: (r) => r.status === 'waitlist',
};

const FILTER_DONE: Record<Filter, string> = {
  all: "Nobody has RSVP'd yet.",
  unpaid: 'Everyone coming has paid.',
  no_wine: 'Everyone coming has entered a wine.',
  no_label: 'Every wine has a label photo.',
  unreviewed: 'Every wine entered so far is proofread.',
  no_letter: 'Every wine has a letter.',
  waitlist: 'Nobody is on the waitlist.',
};

/** The nudges a ticked row can get, in the order the buttons appear. */
const ROW_NUDGES: NudgeReason[] = ['unpaid', 'no_wine', 'no_label'];

interface PendingNudge {
  reason: NudgeReason;
  /** Undefined = everyone currently in that state (the "hasn't replied" nudge). */
  personIds?: string[];
  names: string[];
}

/**
 * The pre-tasting dashboard (route `/tastings/:id/dashboard`): before the night,
 * who has paid, entered a wine, sent a label photo and had it proofread — and
 * at the venue, which letter each bottle is bagged under. Every gap is a
 * filter, and ticked rows can be nudged about it.
 */
export function TastingDashboard() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const [ticked, setTicked] = useState<Set<string>>(() => new Set());
  const [pending, setPending] = useState<PendingNudge | null>(null);

  const {
    data: dash,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['tasting-dashboard', id],
    queryFn: () => dashboardApi.get(id),
    // Several hosts may be working the room at once.
    refetchInterval: 30_000,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['tasting-dashboard', id] });
    void qc.invalidateQueries({ queryKey: ['registrations', id] });
    void qc.invalidateQueries({ queryKey: ['rsvp', id] });
  };

  const nudge = useMutation({
    mutationFn: ({ p, note }: { p: PendingNudge; note: string }) =>
      dashboardApi.nudge(id, { reason: p.reason, personIds: p.personIds, note: note || undefined }),
    onSuccess: (result) => {
      if (result.alreadySent) {
        toast.success('That reminder already went out today, so nothing new was sent.');
      } else if (result.recipientCount === 0) {
        toast.success('Nobody to remind right now.');
      } else {
        toast.success(
          `Reminder sent to ${result.recipientCount} ${result.recipientCount === 1 ? 'person' : 'people'}.`,
        );
      }
      setPending(null);
      setTicked(new Set());
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e, 'Could not send the reminder')),
  });

  const rows = useMemo(() => dash?.rows ?? [], [dash]);
  const shown = useMemo(() => rows.filter(FILTER_TEST[filter]), [rows, filter]);
  // Ticks survive a refetch but never outlive the row they were on.
  const tickedRows = useMemo(
    () => rows.filter((r) => ticked.has(r.registrationId)),
    [rows, ticked],
  );
  const ownerNames = useMemo(
    () => new Map(rows.map((r) => [r.registrationId, `${r.firstName} ${r.lastName}`])),
    [rows],
  );

  if (isLoading) return <p className="text-cream-300/60">Loading…</p>;
  if (error || !dash) {
    return (
      <div className="space-y-4">
        <BackLink id={id} />
        <p className="text-sm text-wine-300">
          {error instanceof ApiError ? error.message : 'Could not load the dashboard.'}
        </p>
      </div>
    );
  }

  const { event, counts } = dash;
  const allShownTicked = shown.length > 0 && shown.every((r) => ticked.has(r.registrationId));
  const toggleTick = (registrationId: string, on: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (on) next.add(registrationId);
      else next.delete(registrationId);
      return next;
    });
  const tickAllShown = (on: boolean) =>
    setTicked((prev) => {
      const next = new Set(prev);
      for (const r of shown) {
        if (on) next.add(r.registrationId);
        else next.delete(r.registrationId);
      }
      return next;
    });
  const pick = (f: Filter) => setFilter((cur) => (cur === f && f !== 'all' ? 'all' : f));

  const nudgeTicked = (reason: NudgeReason) => {
    const reached = tickedRows.filter((r) => r.gaps.includes(reason));
    setPending({
      reason,
      personIds: reached.map((r) => r.personId),
      names: reached.map((r) => `${r.firstName} ${r.lastName}`),
    });
  };

  return (
    <div className="space-y-5 pb-24">
      <div>
        <BackLink id={id} />
        <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-3xl text-gold-200">Tasting dashboard</h1>
              <TestEventBadge isTest={event.isTest} />
            </div>
            <p className="mt-1 text-sm text-cream-300/70">
              {event.title}
              {event.eventDate && <> · {formatDay(event.eventDate)}</>} ·{' '}
              <span className="text-gold-300">{PHASE_LABEL[event.phase] ?? event.phase}</span>
              {event.requiresPayment && event.price && <> · ${event.price} a seat</>}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <RegisterSomeoneButton eventId={id} onChanged={refresh} />
            <Link to={`/live/${id}`} className="btn-ghost px-3 py-1.5 text-sm">
              Live view
            </Link>
            <Button
              variant="quiet"
              className="px-3 py-1.5"
              loading={isFetching}
              onClick={() => void refetch()}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <CountsHeader
        dash={dash}
        filter={filter}
        onPick={pick}
        onRemindNotReplied={() => setPending({ reason: 'not_rsvpd', names: [] })}
      />

      <section aria-label="Registrations" className="md:card md:p-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-1">
          <label className="flex items-center gap-2 text-sm text-cream-200">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-800"
              checked={allShownTicked}
              disabled={shown.length === 0}
              onChange={(e) => tickAllShown(e.target.checked)}
            />
            Tick all {shown.length} shown
          </label>
          <p className="text-xs text-cream-300/55">
            {filter === 'all'
              ? `${rows.length} ${rows.length === 1 ? 'person' : 'people'}`
              : `Showing ${shown.length} of ${rows.length}`}
            {filter !== 'all' && (
              <button
                type="button"
                className="ml-2 text-gold-300 underline-offset-2 hover:underline"
                onClick={() => setFilter('all')}
              >
                Show everyone
              </button>
            )}
          </p>
        </div>

        <div
          className={`hidden border-b border-ink-800 px-2 pb-2 text-[11px] uppercase tracking-wide text-cream-300/50 md:grid md:gap-x-3 ${DASHBOARD_GRID}`}
          aria-hidden="true"
        >
          <span />
          <span>Member</span>
          <span>Payment</span>
          <span>Wine and label</span>
          <span>Proofread</span>
          <span>Letter</span>
          <span />
        </div>

        {shown.length === 0 ? (
          <p className="px-2 py-8 text-center text-sm text-cream-300/60">{FILTER_DONE[filter]}</p>
        ) : (
          <ul className="space-y-2 md:space-y-0">
            {shown.map((row) => (
              <DashboardRow
                key={row.registrationId}
                eventId={id}
                row={row}
                slots={dash.slots}
                ownerNames={ownerNames}
                canAssign={event.phase !== 'final'}
                selected={ticked.has(row.registrationId)}
                onSelect={(on) => toggleTick(row.registrationId, on)}
                onChanged={refresh}
              />
            ))}
          </ul>
        )}

        <p className="mt-3 px-1 text-xs text-cream-300/45">
          Proofreading a wine also approves any producer, region, vineyard or grape the member added
          while entering it.
          {dash.slots.length === 0 && (
            <> Letters appear here once the tasting&apos;s wines are set up.</>
          )}
        </p>
      </section>

      {tickedRows.length > 0 && (
        <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-gold-600/40 bg-ink-900/95 px-3 py-2.5 shadow-2xl backdrop-blur">
          <span className="mr-1 text-sm text-cream-50">{tickedRows.length} ticked</span>
          <span className="text-xs uppercase tracking-wide text-cream-300/50">Remind:</span>
          {ROW_NUDGES.map((reason) => {
            const n = tickedRows.filter((r) => r.gaps.includes(reason)).length;
            if (reason === 'unpaid' && !event.requiresPayment && n === 0) return null;
            return (
              <button
                key={reason}
                type="button"
                className="btn-ghost px-2.5 py-1 text-xs"
                disabled={n === 0}
                title={
                  n === 0
                    ? `None of the ticked rows is "${NUDGE_REASON_LABELS[reason]}"`
                    : undefined
                }
                onClick={() => nudgeTicked(reason)}
              >
                {NUDGE_REASON_LABELS[reason]} ({n})
              </button>
            );
          })}
          <button
            type="button"
            className="btn-quiet ml-auto px-2 py-1 text-xs"
            onClick={() => setTicked(new Set())}
          >
            Clear
          </button>
        </div>
      )}

      <NudgeDialog
        reason={pending?.reason ?? null}
        names={pending?.names ?? []}
        totalCount={pending?.reason === 'not_rsvpd' ? counts.notRsvpd : undefined}
        isTest={event.isTest}
        sending={nudge.isPending}
        onClose={() => setPending(null)}
        onSend={(note) => pending && nudge.mutate({ p: pending, note })}
      />
    </div>
  );
}

function CountsHeader({
  dash,
  filter,
  onPick,
  onRemindNotReplied,
}: {
  dash: Dashboard;
  filter: Filter;
  onPick: (f: Filter) => void;
  onRemindNotReplied: () => void;
}) {
  const { counts, event } = dash;
  const noLetter = dash.rows.filter(FILTER_TEST.no_letter).length;
  return (
    <div
      role="group"
      aria-label="Filter by what's missing"
      className="grid grid-cols-4 gap-2 lg:grid-cols-8"
    >
      <CountTile
        label="Coming"
        value={counts.registered}
        sub={event.capacity != null ? `of ${event.capacity}` : undefined}
        active={filter === 'all'}
        onClick={() => onPick('all')}
      />
      <CountTile
        label="Waitlist"
        value={counts.waitlist}
        active={filter === 'waitlist'}
        onClick={() => onPick('waitlist')}
      />
      {(event.requiresPayment || counts.unpaid > 0) && (
        <CountTile
          label="Not paid"
          value={counts.unpaid}
          tone="gap"
          active={filter === 'unpaid'}
          onClick={() => onPick('unpaid')}
        />
      )}
      <CountTile
        label="No wine"
        value={counts.noWine}
        tone="gap"
        active={filter === 'no_wine'}
        onClick={() => onPick('no_wine')}
      />
      <CountTile
        label="No label"
        value={counts.noLabel}
        tone="gap"
        active={filter === 'no_label'}
        onClick={() => onPick('no_label')}
      />
      <CountTile
        label="Not proofread"
        value={counts.unreviewed}
        tone="gap"
        active={filter === 'unreviewed'}
        onClick={() => onPick('unreviewed')}
      />
      {dash.slots.length > 0 && (
        <CountTile
          label="No letter"
          value={noLetter}
          tone="gap"
          active={filter === 'no_letter'}
          onClick={() => onPick('no_letter')}
        />
      )}
      <CountTile
        label="Haven't replied"
        value={counts.notRsvpd}
        action={
          counts.notRsvpd > 0 ? (
            <button
              type="button"
              className="mt-1.5 text-[11px] text-gold-300 underline-offset-2 hover:underline"
              onClick={onRemindNotReplied}
            >
              Remind them
            </button>
          ) : null
        }
      />
    </div>
  );
}

function BackLink({ id }: { id: string }) {
  return (
    <Link to={`/tastings/${id}`} className="text-xs text-cream-300/60 hover:text-gold-200">
      ← Back to the tasting
    </Link>
  );
}

const formatDay = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
